import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Smartphone, Search, Printer, Ban, Plus } from 'lucide-react';
import BulkAppKennungen from '@/components/app/BulkAppKennungen';
import { kennungCreatedMessage } from '@/components/app/PersonAppKennungen';

const TYPE_LABEL = { student: 'Schüler:in', guardian: 'Zahler:in', teacher: 'Lehrkraft', admin: 'Verwaltung' };

export default function KennungenTab() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [personQ, setPersonQ] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [creating, setCreating] = useState(false);
  const [printItem, setPrintItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-identities', { params: { search: search || undefined } });
      setList(r.data || []);
    } catch (e) { toast.error('Kennungen konnten nicht geladen werden'); }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (personQ.length < 2) { setPersonResults([]); return; }
    const t = setTimeout(() => {
      api.get('/admin/app-identities/person-search', { params: { q: personQ } })
        .then((r) => setPersonResults(r.data || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [personQ]);

  const create = async (p) => {
    setCreating(true);
    try {
      const r = await api.post('/admin/app-identities', { person_type: p.person_type, person_id: p.person_id });
      toast.success(kennungCreatedMessage(r.data));
      setPersonQ(''); setPersonResults([]);
      await load();
      setPrintItem(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen');
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/app-identities/${id}`);
      toast.success('Kennung widerrufen');
      load();
    } catch (e) { toast.error('Widerrufen fehlgeschlagen'); }
  };

  const doPrint = () => window.print();

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <Smartphone className="w-6 h-6 text-brand-red" />
            <h1 className="text-2xl font-bold text-slate-900">App-Kennungen</h1>
          </div>
          <BulkAppKennungen personType="all" audienceLabel="Personen" />
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Zugangscodes für die Mobile-App erzeugen (Kennung + PIN). Nutzer schalten damit in der App
          unter „Einstellungen → Kennungen“ den Zugriff auf ihre Person frei.
        </p>

        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Neue Kennung erzeugen</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input className="pl-9" placeholder="Person suchen (Schüler, Zahler, Lehrkraft)…"
                   value={personQ} onChange={(e) => setPersonQ(e.target.value)}
                   data-testid="app-identities-person-search" />
          </div>
          {personResults.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-64 overflow-y-auto">
              {personResults.map((p) => (
                <div key={`${p.person_type}-${p.person_id}`} className="flex items-center justify-between px-3 py-2">
                  <div>
                    <span className="text-sm font-medium">{p.name}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{p.detail}</Badge>
                  </div>
                  <Button size="sm" disabled={creating} onClick={() => create(p)}
                          data-testid={`app-identities-create-${p.person_id}`}>
                    <Plus className="w-4 h-4 mr-1" /> Kennung
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input className="pl-9" placeholder="Kennungen durchsuchen…" value={search}
                   onChange={(e) => setSearch(e.target.value)} data-testid="app-identities-search" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="p-3 text-left">Person</th>
                <th className="p-3 text-left">Typ</th>
                <th className="p-3 text-left">Kennung</th>
                <th className="p-3 text-left">PIN</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((i) => (
                <tr key={i.id} data-testid={`app-identity-row-${i.id}`}>
                  <td className="p-3 font-medium">{i.person_name}</td>
                  <td className="p-3 text-slate-500">{TYPE_LABEL[i.person_type]}</td>
                  <td className="p-3 font-mono">{i.code}</td>
                  <td className="p-3 font-mono">{i.pin || '••••••'}</td>
                  <td className="p-3">
                    {i.revoked ? <Badge variant="destructive">Widerrufen</Badge>
                      : i.claimed ? <Badge className="bg-brand-success/10 text-brand-success hover:bg-brand-success/10">Aktiviert</Badge>
                        : <Badge variant="outline">Offen</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    {!i.revoked && !i.claimed && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setPrintItem(i)}
                                data-testid={`app-identity-print-${i.id}`}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => revoke(i.id)}
                                data-testid={`app-identity-revoke-${i.id}`}>
                          <Ban className="w-4 h-4 text-rose-500" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!list.length && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">Noch keine Kennungen erzeugt</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <Dialog open={!!printItem} onOpenChange={(o) => !o && setPrintItem(null)}>
          <DialogContent className="max-w-sm" data-testid="app-identity-print-dialog">
            <DialogHeader>
              <DialogTitle>App-Zugang</DialogTitle>
              <DialogDescription>Zum Ausdrucken oder Abfotografieren — QR-Code in der App scannen oder Kennung + PIN eintippen.</DialogDescription>
            </DialogHeader>
            {printItem && (
              <div className="print-card flex flex-col items-center gap-3 py-4">
                <p className="font-semibold">{printItem.person_name}</p>
                <QRCodeSVG value={JSON.stringify({ code: printItem.code, pin: printItem.pin })} size={160} />
                <div className="text-center">
                  <p className="font-mono text-lg tracking-widest">{printItem.code}</p>
                  <p className="font-mono text-sm text-slate-500">PIN: {printItem.pin}</p>
                </div>
                <p className="text-xs text-slate-400 text-center">
                  App öffnen → Registrieren → Einstellungen → Kennungen → „Kennung hinzufügen“
                </p>
              </div>
            )}
            <Button onClick={doPrint} data-testid="app-identity-do-print">
              <Printer className="w-4 h-4 mr-2" /> Drucken
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
